// Drawing. Knows nothing about the rules — hand it a game and it paints it.
//
// The maze is painted once into an offscreen canvas and blitted, and the pizza
// slices are pre-rendered into a handful of rotated sprites. Two hundred and
// fifty slices re-pathed every frame is the difference between sixty frames a
// second and thirty on a phone, and nothing about them changes between frames.
//
// Everything is sized from the board, so the small board draws the same art at
// half again the size rather than the same art with more empty screen round it.

const C = {
  bg: '#080b16',
  wall: '#101a35',
  line: '#2fd4ee',
  crust: '#f0a545',
  cheese: '#ffd868',
  pepp: '#d8331f',
  raf: '#ff2d95',
  // Frightened is GREEN, not the pale blue that flashes white. Green reads as
  // "safe to eat" on its own, and the blue-then-white warning is the single
  // most recognisable thing about somebody else's frightened ghost.
  rafPale: '#35c46f',
  rafPaleAlt: '#b9f5cf',
  rafFace: '#16233d',
  nick: '#ffd23f',
  toque: '#fff6e0',
  toqueBand: '#e8c96a',
  mouth: '#5a2410',
  pieCrust: '#ffb02e',
  pieFill: '#ff2d95',
  pieLattice: '#ffe0a6',
  eye: '#ffffff',
  pupil: '#0b1020',
};

const SLICE_ANGLES = [-18, -9, 0, 9, 18];

/** How much of a tile an exposed wall corner is cut back by, at 45 degrees. */
const CHAMFER = 0.4;
const rad = (deg) => (deg * Math.PI) / 180;

function surface(w, h, dpr) {
  const c = document.createElement('canvas');
  c.width = Math.ceil(w * dpr);
  c.height = Math.ceil(h * dpr);
  const ctx = c.getContext('2d');
  ctx.scale(dpr, dpr);
  return { canvas: c, ctx };
}

/** Toppings. Pepperoni by default; basil and olive for the vegan slice. */
const TOPPINGS = {
  meat: [C.pepp, C.pepp, C.pepp],
  vegan: ['#3f9e52', '#1d6b33', '#3f9e52'],
};

/** One pizza slice, centred on the origin. Crust over the top, three toppings. */
function slicePath(ctx, R, vegan = false) {
  const off = R * 0.54;
  const half = rad(39);
  const t = R * 0.33;
  const a0 = -Math.PI / 2 - half;
  const a1 = -Math.PI / 2 + half;

  ctx.beginPath();
  ctx.moveTo(0, off);
  ctx.arc(0, off, R, a0, a1);
  ctx.closePath();
  ctx.fillStyle = C.cheese;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, off, R, a0, a1);
  ctx.arc(0, off, R - t, a1, a0, true);
  ctx.closePath();
  ctx.fillStyle = C.crust;
  ctx.fill();

  const p = R / 10.4;
  const colours = vegan ? TOPPINGS.vegan : TOPPINGS.meat;
  // Green on yellow is a lower-contrast pair than red on yellow, so the vegan
  // pieces are drawn a little larger and rimmed to hold their shape at 20px.
  const grow = vegan ? 1.12 : 1;
  [[0, 1.6, 1.7], [-2.6, -1.4, 1.6], [2.6, -1.4, 1.6]].forEach(([px, py, pr], i) => {
    ctx.beginPath();
    ctx.arc(px * p, py * p, pr * p * grow, 0, Math.PI * 2);
    ctx.fillStyle = colours[i];
    ctx.fill();
    if (vegan) {
      ctx.lineWidth = Math.max(0.6, p * 0.42);
      ctx.strokeStyle = '#14512a';
      ctx.stroke();
    }
  });
}

/** The prize: lattice-topped, and deliberately larger than a slice. */
function drawPie(ctx, x, y, k, scale = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(k * scale, k * scale);
  ctx.shadowColor = C.pieFill;
  ctx.shadowBlur = 14;

  ctx.beginPath();
  ctx.ellipse(0, 3.4, 10.6, 4.7, 0, 0, Math.PI * 2);
  ctx.fillStyle = C.pieCrust;
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-10.6, 3.4);
  ctx.arc(0, 3.4, 10.6, Math.PI, 0);
  ctx.closePath();
  ctx.fillStyle = C.pieFill;
  ctx.fill();

  ctx.shadowBlur = 0;
  ctx.strokeStyle = C.pieLattice;
  ctx.lineWidth = 1.7;
  ctx.lineCap = 'round';
  for (const [ax, ay, bx, by] of [
    [-6.2, -2.7, 6.2, -2.7], [-8.4, 0.4, 8.4, 0.4],
    [-3.7, 3.4, -3.7, -6.6], [3.7, 3.4, 3.7, -6.6],
  ]) {
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  }
  ctx.restore();
}

function nickAura(ctx, r, aura) {
  if (!aura) return;
  ctx.strokeStyle = C.pieFill;
  ctx.globalAlpha = 0.9;
  ctx.lineWidth = 1.8;
  ctx.beginPath(); ctx.arc(0, 0, r + 4.2 + aura * 1.6, 0, Math.PI * 2); ctx.stroke();
  ctx.globalAlpha = 0.4;
  ctx.lineWidth = 1.2;
  ctx.beginPath(); ctx.arc(0, 0, r + 8.4 + aura * 3, 0, Math.PI * 2); ctx.stroke();
  ctx.globalAlpha = 1;
}

/**
 * How far each half of a pictured head swings, in radians.
 *
 * Lower than it would be for a centre pivot: hinged at the back edge, the same
 * angle throws the FRONT of the head much further, so 0.30 tore the face open.
 */
const MAX_JAW = 0.2;

/**
 * Nick, drawn: a chef's head under a toque.
 *
 * The mouth is PAINTED ON rather than cut out of the outline. That is the whole
 * point of the shape — a circle with a moving wedge removed from its silhouette
 * is somebody else's registered character, and no amount of recolouring changes
 * what the outline says. Here the silhouette is a whole head at every point in
 * the chew, and the eating reads from the mouth instead.
 *
 * Nothing rotates, either. He looks and chews toward where he is going by
 * shifting his features that way, which keeps the hat on top of his head when
 * he is running left.
 */
function drawNick(ctx, x, y, dir, phase, aura, r) {
  const open = 0.5 + 0.5 * Math.sin(phase);
  const s = r / 10.4;
  const hr = r * 0.82;
  const cy = r * 0.17;                           // head sits low; the hat needs the room
  const fx = (dir?.x ?? 0) * hr * 0.30;
  const fy = (dir?.y ?? 0) * hr * 0.20;

  ctx.save();
  ctx.translate(x, y);
  nickAura(ctx, r, aura);

  ctx.shadowColor = C.nick;
  ctx.shadowBlur = 12;
  ctx.beginPath();
  ctx.arc(0, cy, hr, 0, Math.PI * 2);
  ctx.fillStyle = C.nick;
  ctx.fill();
  ctx.shadowBlur = 0;

  // Three puffs and a band. At twenty pixels that is the whole hat.
  ctx.fillStyle = C.toque;
  for (const [px, pr] of [[-hr * 0.54, hr * 0.40], [0, hr * 0.52], [hr * 0.54, hr * 0.40]]) {
    ctx.beginPath();
    ctx.arc(px, cy - hr * 0.88, pr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillRect(-hr * 0.84, cy - hr * 0.96, hr * 1.68, hr * 0.44);
  ctx.fillStyle = C.toqueBand;
  ctx.fillRect(-hr * 0.88, cy - hr * 0.62, hr * 1.76, hr * 0.24);

  ctx.fillStyle = C.pupil;
  for (const ex of [-0.34, 0.34]) {
    ctx.beginPath();
    ctx.arc(ex * hr + fx * 0.5, cy - hr * 0.10 + fy * 0.5, 1.9 * s, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.beginPath();
  ctx.ellipse(fx, cy + hr * 0.44 + fy, hr * 0.30, hr * (0.09 + 0.32 * open), 0, 0, Math.PI * 2);
  ctx.fillStyle = C.mouth;
  ctx.fill();
  ctx.restore();
}

/**
 * Nick as the picture and nothing else: the head splits and the jaw swings open.
 *
 * The two halves ROTATE about the centre rather than sliding apart, so the gap
 * is a wedge that opens toward where he is going — a mouth, not a letterbox
 * slot. Rotating a half-disc about the centre opens the front by twice the
 * angle and overlaps the back by the same, which is why the back never shows a
 * seam.
 *
 * The direction rotation is undone before the picture is drawn, so a face never
 * ends up upside down running left. What is left is the jaw's own tilt, which
 * is the part that reads as chewing.
 */
function drawNickSplit(ctx, x, y, dir, phase, square, aura, r) {
  const angle = dir ? Math.atan2(dir.y, dir.x) : 0;
  const jaw = (0.5 + 0.5 * Math.sin(phase)) * MAX_JAW;
  ctx.save();
  ctx.translate(x, y);
  nickAura(ctx, r, aura);
  ctx.rotate(angle);
  for (const half of [-1, 1]) {
    ctx.save();
    // Hinge at the BACK of the head rather than the middle. Pivoting about the
    // centre opens the front and overlaps the back by the same amount, which
    // works for a plain disc but on a face reads as the head sliding apart.
    // Pivoting at the back edge keeps the two halves joined where a jaw is
    // joined, and everything that moves is the front.
    ctx.translate(-r, 0);
    ctx.rotate(half * jaw);
    ctx.translate(r, 0);
    ctx.beginPath();
    // Generously oversized: this is a half-plane, and it must not clip the
    // corners of the picture once the half has swung.
    ctx.rect(-r * 2, half < 0 ? -r * 2 : 0, r * 4, r * 2);
    ctx.clip();
    ctx.rotate(-angle);
    ctx.drawImage(square, -r, -r, r * 2, r * 2);
    ctx.restore();
  }
  ctx.restore();
}

/** A rounded square, centred on the origin. Raf's head, and his ghost-of. */
function headPath(g, w, rad) {
  g.beginPath();
  g.moveTo(-w + rad, -w);
  g.lineTo(w - rad, -w);
  g.quadraticCurveTo(w, -w, w, -w + rad);
  g.lineTo(w, w - rad);
  g.quadraticCurveTo(w, w, w - rad, w);
  g.lineTo(-w + rad, w);
  g.quadraticCurveTo(-w, w, -w, w - rad);
  g.lineTo(-w, -w + rad);
  g.quadraticCurveTo(-w, -w, -w + rad, -w);
  g.closePath();
}

/**
 * Raf, drawn: a rounded head.
 *
 * Not a dome with a wavy hem. That silhouette, the blue-then-flashing-white
 * warning and a pair of disembodied eyes drifting home are the three most
 * recognisable things about the ghosts in a game this one should not be
 * mistaken for. So: a head, green when he is edible, and a faint outline of
 * that same head walking back when he has been eaten.
 */
function drawRaf(ctx, x, y, dir, mode, frightLeft, t, r) {
  const gone = mode === 'eaten';
  const scared = mode === 'fright';
  const flashing = scared && frightLeft < 2 && Math.floor(t * 6) % 2 === 0;
  const body = scared ? (flashing ? C.rafPaleAlt : C.rafPale) : C.raf;
  const s = r / 10.4;
  const w = r * 0.9;
  const rad = r * 0.46;

  ctx.save();
  ctx.translate(x, y);

  if (gone) {
    ctx.globalAlpha = 0.32;
    ctx.strokeStyle = C.line;
    ctx.lineWidth = 1.7;
    ctx.lineJoin = 'round';
    headPath(ctx, w, rad);
    ctx.stroke();
    ctx.restore();
    return;
  }

  ctx.shadowColor = body;
  ctx.shadowBlur = scared ? 8 : 14;
  headPath(ctx, w, rad);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.shadowBlur = 0;

  if (scared) {
    // Wide eyes and a flat worried line, rather than a zigzag grimace.
    ctx.fillStyle = C.rafFace;
    for (const ex of [-3.6, 3.6]) {
      ctx.beginPath(); ctx.arc(ex * s, -2.2 * s, 2.1 * s, 0, Math.PI * 2); ctx.fill();
    }
    ctx.strokeStyle = C.rafFace;
    ctx.lineWidth = 1.7;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-3.6 * s, 3.4 * s);
    ctx.lineTo(3.6 * s, 3.4 * s);
    ctx.stroke();
  } else {
    const lx = (dir?.x ?? 0) * 1.1 * s;
    const ly = (dir?.y ?? 0) * 1.1 * s;
    for (const ex of [-3.5, 3.5]) {
      ctx.beginPath(); ctx.arc(ex * s, -1.6 * s, 3.0 * s, 0, Math.PI * 2);
      ctx.fillStyle = C.eye; ctx.fill();
      ctx.beginPath(); ctx.arc(ex * s + lx, -1.6 * s + ly, 1.5 * s, 0, Math.PI * 2);
      ctx.fillStyle = C.pupil; ctx.fill();
    }
  }
  ctx.restore();
}

/** Scale a picture to COVER a square, centred — cropping beats letterboxing. */
function squareOf(img, size, dpr) {
  const s = surface(size, size, dpr);
  const ratio = img.naturalWidth / img.naturalHeight;
  const w = ratio >= 1 ? size * ratio : size;
  const h = ratio >= 1 ? size : size / ratio;
  s.ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
  return s.canvas;
}

/**
 * Raf sprites from a picture: the picture, tinted for the frightened states.
 * No ghost body behind it — a photo is what was asked for.
 */
function buildRafSprites(img, dpr, px) {
  const make = (body, tint) => {
    const s = surface(px, px, dpr);
    const g = s.ctx;
    const c = px / 2;

    g.save();
    g.translate(c, c);
    const fw = px * 0.98;
    const fh = fw * (img.naturalHeight / img.naturalWidth);
    g.drawImage(img, -fw / 2, -fh / 2, fw, fh);

    if (tint) {
      // Tint only what is already painted, so the transparent corners of a
      // cut-out stay transparent.
      g.globalCompositeOperation = 'source-atop';
      g.fillStyle = tint;
      g.fillRect(-px, -px, px * 2, px * 2);
      g.globalCompositeOperation = 'source-over';
    }
    g.restore();
    return s.canvas;
  };
  return {
    hunting: make(C.raf, null),
    scared: make(C.rafPale, 'rgba(53, 196, 111, 0.62)'),
    flash: make(C.rafPaleAlt, 'rgba(185, 245, 207, 0.55)'),
  };
}

export function createRenderer(canvas, board, { vegan = false } = {}) {
  const TILE = board.tile;
  const WIDTH = board.cols * TILE;
  const HEIGHT = board.rows * TILE;
  // Capped at 2, not 3. A dpr-3 phone allocates and paints five megapixels
  // before the first frame, for art made of flat fills and small sprites that
  // nobody can tell apart from the dpr-2 version.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const k = TILE / 24;                       // everything is drawn relative to this
  const R_CHAR = 10.4 * k;
  const R_SLICE = 9.7 * k;
  const FACE_PX = Math.round(72 * k);

  canvas.width = WIDTH * dpr;
  canvas.height = HEIGHT * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  // --- the maze, painted once -------------------------------------------
  const maze = surface(WIDTH, HEIGHT, dpr);
  (function paintMaze(g) {
    g.fillStyle = C.bg;
    g.fillRect(0, 0, WIDTH, HEIGHT);
    // Two passes over the same cells: grown in the line colour, then flat in
    // the fill. Each cell overlaps its wall neighbours, so a bar of three cells
    // outlines as one shape instead of three boxes in a row. The signs matter:
    // negating the overlap shrinks each cell AWAY from its neighbour, leaving a
    // 1px seam that the outline pass paints cyan — which renders the whole
    // maze as graph paper.
    for (const [grow, colour] of [[1.7 * k, C.line], [0, C.wall]]) {
      g.fillStyle = colour;
      for (let y = 0; y < board.rows; y += 1) {
        for (let x = 0; x < board.cols; x += 1) {
          if (board.layout[y][x] !== '#') continue;
          const px = x * TILE;
          const py = y * TILE;
          const solid = (cx, cy) => cy >= 0 && cy < board.rows
            && board.layout[cy][board.wrapX(cx)] === '#';
          const l = px - (solid(x - 1, y) ? 0.5 : grow);
          const r = px + TILE + (solid(x + 1, y) ? 0.5 : grow);
          const t = py - (solid(x, y - 1) ? 0.5 : grow);
          const b = py + TILE + (solid(x, y + 1) ? 0.5 : grow);
          // Cut every EXPOSED corner back at 45 degrees. A corner between two
          // walls stays square so bars still merge into one shape; a corner
          // with corridor on both sides becomes a bevel, and the maze turns
          // through eight angles instead of four. It is the cheapest way to
          // stop a grid of blue boxes reading as the grid of blue boxes —
          // nothing about the board or the movement changes, only the shape
          // the walls are painted as.
          const cut = Math.min(TILE * CHAMFER, (r - l) / 2, (b - t) / 2);
          const tl = !solid(x - 1, y) && !solid(x, y - 1);
          const tr = !solid(x + 1, y) && !solid(x, y - 1);
          const br = !solid(x + 1, y) && !solid(x, y + 1);
          const bl = !solid(x - 1, y) && !solid(x, y + 1);
          g.beginPath();
          g.moveTo(l + (tl ? cut : 0), t);
          g.lineTo(r - (tr ? cut : 0), t);
          if (tr) g.lineTo(r, t + cut);
          g.lineTo(r, b - (br ? cut : 0));
          if (br) g.lineTo(r - cut, b);
          g.lineTo(l + (bl ? cut : 0), b);
          if (bl) g.lineTo(l, b - cut);
          g.lineTo(l, t + (tl ? cut : 0));
          g.closePath();
          g.fill();
        }
      }
    }
  }(maze.ctx));

  // --- pizza slices, five rotations -------------------------------------
  const SPRITE = Math.round(32 * k);
  const slices = SLICE_ANGLES.map((deg) => {
    const s = surface(SPRITE, SPRITE, dpr);
    s.ctx.translate(SPRITE / 2, SPRITE / 2);
    s.ctx.rotate(rad(deg));
    slicePath(s.ctx, R_SLICE, vegan);
    return s.canvas;
  });
  const sliceFor = (x, y) => slices[(x * 7 + y * 13) % slices.length];

  // --- uploaded faces ----------------------------------------------------
  let rafSprites = null;
  let nickSquare = null;
  // How big a picture is drawn, as a multiple of the drawn character's radius.
  //
  // Raf's picture used to be blitted at the SPRITE's own resolution (72 units)
  // while Nick's was drawn at his radius (about 21), so a photographed Raf came
  // out three and a half times the size of a photographed Nick. Both now use
  // this one number, so they cannot drift apart again.
  const scales = { nick: 1, raf: 1 };
  const faceRadius = (who) => R_CHAR * (scales[who] || 1);

  /**
   * A picture replaces the character; no picture leaves the drawn one.
   *
   * There used to be a Shapes / Photo only switch. It asked a question that
   * answers itself: somebody who has gone to the trouble of choosing a photo
   * wants to see the photo, and somebody who has not has nothing to put behind
   * a silhouette anyway.
   */
  function setFaces({ nick, raf, nickScale, rafScale } = {}) {
    if (nickScale > 0) scales.nick = nickScale;
    if (rafScale > 0) scales.raf = rafScale;
    rafSprites = null;
    nickSquare = null;
    if (raf) {
      const img = new window.Image();
      img.onload = () => { rafSprites = buildRafSprites(img, dpr, FACE_PX); };
      img.src = raf;
    }
    if (nick) {
      const img = new window.Image();
      img.onload = () => { nickSquare = squareOf(img, FACE_PX, dpr); };
      img.src = nick;
    }
  }

  const px = (v) => v * TILE + TILE / 2;

  function paintNick(cx, cy, dir, mouth, aura) {
    if (!nickSquare) { drawNick(ctx, cx, cy, dir, mouth, aura, R_CHAR); return; }
    drawNickSplit(ctx, cx, cy, dir, mouth, nickSquare, aura, faceRadius('nick'));
  }

  /**
   * A ring on the Raf a second player is driving.
   *
   * Drawn UNDER the ghost rather than over it, so it reads as a spotlight the
   * character is standing in rather than a box drawn on top of one — and in
   * cyan, the one accent neither Nick (gold) nor Raf (pink) already owns, so it
   * cannot be mistaken for a game state.
   */
  function paintDriverRing(cx, cy, flash, r) {
    const pulse = 0.5 + 0.5 * Math.sin(flash * 4.5);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.shadowColor = C.line;
    ctx.shadowBlur = 12 + pulse * 8;
    ctx.strokeStyle = C.line;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.85;
    ctx.beginPath();
    ctx.arc(0, 0, r + 3.5 + pulse * 1.6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1.2;
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(0, 0, r + 8 + pulse * 3, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  function paintRaf(raf, mode, frightLeft, flash) {
    const cx = px(raf.x);
    const cy = px(raf.y);
    const m = raf.eaten ? 'eaten' : mode;
    if (raf.player) paintDriverRing(cx, cy, flash, rafSprites ? faceRadius('raf') : R_CHAR);
    if (!rafSprites || m === 'eaten') {
      drawRaf(ctx, cx, cy, raf.dir, m, frightLeft, flash, R_CHAR);
      return;
    }
    const scared = m === 'fright';
    const flashing = scared && frightLeft < 2 && Math.floor(flash * 6) % 2 === 0;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.shadowColor = scared ? C.rafPale : C.raf;
    ctx.shadowBlur = scared ? 9 : 15;
    // Drawn at the SAME radius Nick's picture uses, not at the sprite's own
    // resolution — the sprite is rendered large only so it stays sharp.
    const d = faceRadius('raf') * 2;
    ctx.drawImage(scared ? (flashing ? rafSprites.flash : rafSprites.scared) : rafSprites.hunting,
      -d / 2, -d / 2, d, d);
    ctx.restore();
  }

  function draw(game) {
    ctx.clearRect(0, 0, WIDTH, HEIGHT);
    ctx.drawImage(maze.canvas, 0, 0, WIDTH, HEIGHT);

    for (const [key, what] of game.food) {
      const [fx, fy] = key.split(',');
      const x = Number(fx);
      const y = Number(fy);
      if (what === 'pizza') {
        ctx.drawImage(sliceFor(x, y), px(x) - SPRITE / 2, px(y) - SPRITE / 2, SPRITE, SPRITE);
      } else {
        drawPie(ctx, px(x), px(y), k, 1 + Math.sin(game.flash * 3.4) * 0.07);
      }
    }

    if (game.state !== 'dying') {
      for (const raf of game.rafs) paintRaf(raf, game.mode, game.frightLeft, game.flash);
    }

    const aura = game.mode === 'fright' ? 0.5 + 0.5 * Math.sin(game.flash * 7) : 0;
    if (game.state === 'dying') {
      // Nick folds in on himself rather than simply vanishing.
      const t = Math.max(0, Math.min(1, 1 - game.timer / 1.4));
      ctx.save();
      ctx.translate(px(game.nick.x), px(game.nick.y));
      ctx.rotate(t * Math.PI * 2);
      ctx.globalAlpha = 1 - t;
      ctx.scale(1 - t * 0.8, 1 - t * 0.8);
      paintNick(0, 0, game.nick.dir, game.mouth, 0);
      ctx.restore();
    } else {
      paintNick(px(game.nick.x), px(game.nick.y), game.nick.dir, game.mouth, aura);
    }
  }

  return { draw, setFaces, width: WIDTH, height: HEIGHT };
}
